import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialPanelComerciales } from './panel_comerciales.initial';
import {
    loadSnapshot,
    saveSellerConfigThunk,
    saveVipOverrideThunk,
} from './thunks';
import {
    AuditEntry,
    Commercial,
    Team,
    VipAccount,
} from 'src/components/Retention/PanelComerciales/PanelComerciales.types';

export const panelComercialesSlice = createSlice({
    name: 'panel_comerciales',
    initialState: initialPanelComerciales,
    reducers: {
        upsertTeam: (state, action: PayloadAction<Team>) => {
            const idx = state.snapshot.teams.findIndex((t) => t.name === action.payload.name);
            if (idx >= 0) state.snapshot.teams[idx] = action.payload;
            else state.snapshot.teams.push(action.payload);
        },
        renameTeam: (state, action: PayloadAction<{ from: string; to: string }>) => {
            const { from, to } = action.payload;
            if (from === to) return;
            state.snapshot.teams = state.snapshot.teams.map((t) =>
                t.name === from ? { ...t, name: to, leader: t.leader === from ? to : t.leader } : t,
            );
            state.snapshot.commercials = state.snapshot.commercials.map((c) =>
                c.team === from ? { ...c, team: to } : c,
            );
            state.snapshot.vips = state.snapshot.vips.map((v) =>
                v.team === from ? { ...v, team: to } : v,
            );
        },
        deactivateTeam: (state, action: PayloadAction<string>) => {
            state.snapshot.teams = state.snapshot.teams.map((t) =>
                t.name === action.payload ? { ...t, active: false } : t,
            );
        },
        upsertCommercial: (state, action: PayloadAction<Commercial>) => {
            const idx = state.snapshot.commercials.findIndex((c) => c.id === action.payload.id);
            if (idx >= 0) state.snapshot.commercials[idx] = action.payload;
            else state.snapshot.commercials.push(action.payload);
        },
        upsertVip: (state, action: PayloadAction<VipAccount>) => {
            const idx = state.snapshot.vips.findIndex((v) => v.id === action.payload.id);
            if (idx >= 0) state.snapshot.vips[idx] = action.payload;
            else state.snapshot.vips.push(action.payload);
        },
        removeVip: (state, action: PayloadAction<string>) => {
            state.snapshot.vips = state.snapshot.vips.filter((v) => v.id !== action.payload);
        },
        appendAuditLog: (state, action: PayloadAction<AuditEntry>) => {
            state.snapshot.auditLog = [action.payload, ...state.snapshot.auditLog];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadSnapshot.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(loadSnapshot.fulfilled, (state, action) => {
            state.loading = false;
            state.snapshot = action.payload;
        });
        builder.addCase(loadSnapshot.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message ?? 'Error cargando snapshot';
        });

        builder.addCase(saveSellerConfigThunk.pending, (state) => {
            state.saving = true;
        });
        builder.addCase(saveSellerConfigThunk.fulfilled, (state) => {
            state.saving = false;
        });
        builder.addCase(saveSellerConfigThunk.rejected, (state, action) => {
            state.saving = false;
            state.error = action.error.message ?? 'Error guardando configuración';
        });

        builder.addCase(saveVipOverrideThunk.pending, (state) => {
            state.saving = true;
        });
        builder.addCase(saveVipOverrideThunk.fulfilled, (state) => {
            state.saving = false;
        });
        builder.addCase(saveVipOverrideThunk.rejected, (state, action) => {
            state.saving = false;
            state.error = action.error.message ?? 'Error guardando VIP';
        });
    },
});

export const {
    upsertTeam,
    renameTeam,
    deactivateTeam,
    upsertCommercial,
    upsertVip,
    removeVip,
    appendAuditLog,
} = panelComercialesSlice.actions;

export default panelComercialesSlice.reducer;
